import { api } from "@todotogether/backend/_generated/api";
import {
  useCalendarExportFlow,
  type CalendarExportCallbacks,
  type ExportRequest,
} from "@todotogether/shared/calendarExportFlow";

/** Which export the viewer asked for — the shared vocabulary, re-exported. */
export type { ExportRequest };

/** The callbacks vocabulary is the shared flow's (onShared stays web-unpassed). */
export type ExportCallbacks = CalendarExportCallbacks;

/**
 * The web seam, one module-level constant: the generated export query
 * references and the platform delivery — the browser download, the
 * share-sheet twin. Constant by design (#656): the flow's settle
 * effect keys on the seam's identity, and a per-render object would
 * re-fire it mid-flight.
 */
const webExportSeam = {
  listCalendar: api.exports.listCalendar,
  itemCalendar: api.exports.itemCalendar,
  deliver: ({ filename, ics }: { filename: string; ics: string }) =>
    downloadIcs(filename, ics),
};

/**
 * The on-tap half of calendar export on the web (issue #375) — the
 * thin web binding of the ONE arming flow (#656, itself the mobile
 * hook's #374 shape): the shared useCalendarExportFlow carries the
 * arming/settle/in-flight logic and the device timezone; only the
 * platform delivery stays app-side.
 */
export function useCalendarExport(callbacks: ExportCallbacks) {
  return useCalendarExportFlow(callbacks, webExportSeam);
}

/**
 * Trigger a browser download of the ICS text under the SERVER-computed
 * filename (sanitization lives in exactly one place — icsFilenameFor):
 * a one-shot object URL on a download-anchor click, revoked right
 * after. The mime type types the blob so the browser hands the file to
 * its calendar app — the web twin of the native share sheet.
 */
function downloadIcs(filename: string, ics: string): void {
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
